// Initial confirmation pane: shows avatar details + explicit Start button.
// Without this, the wizard auto-starts on mount and the operator stares at a
// vague spinner with no indication of what's happening — exactly the feedback
// from the live test ("I clicked, nothing happened").

const PLATFORM_LABEL: Record<string, string> = {
  whatsapp: "WhatsApp",
  telegram: "Telegram",
  signal: "Signal",
};

export function IntroStep({
  avatarName,
  platform,
  status,
  expectedGroups,
  isPending,
  error,
  onStart,
  onCancel,
}: {
  avatarName: string;
  platform: string;
  status: string;
  expectedGroups: number;
  isPending: boolean;
  /** Backend error from the start attempt (e.g. 409 concurrent session, 400
   *  not-eligible). Shown inline so a failed Start is never silent. */
  error?: string | null;
  onStart: () => void;
  onCancel: () => void;
}) {
  // A never-linked avatar (status='new') is being connected for the first time,
  // not relinked — there's no existing session to wipe. Telegram sends a login
  // CODE the operator types; WhatsApp surfaces a pairing code.
  const isOnboard = status === "new";
  const platformLabel = PLATFORM_LABEL[platform] ?? platform;
  const codeWord = platform === "telegram" ? "login code" : "pairing code";
  const verb = isOnboard ? "Connect" : "Start Relink";
  return (
    <div style={{ padding: 24 }}>
      <div
        style={{
          background: "var(--iris-bg-3)",
          border: "1px solid var(--iris-line-2)",
          borderRadius: 8,
          padding: 16,
          marginBottom: 16,
        }}
      >
        <div className="iris-meta" style={{ marginBottom: 4 }}>
          Avatar
        </div>
        <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 12 }}>{avatarName}</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, fontSize: 12 }}>
          <div>
            <div className="iris-meta">Platform</div>
            <div>{PLATFORM_LABEL[platform] ?? platform}</div>
          </div>
          <div>
            <div className="iris-meta">Current status</div>
            <div>{status}</div>
          </div>
          <div>
            <div className="iris-meta">Expected groups</div>
            <div>{expectedGroups}</div>
          </div>
          <div>
            <div className="iris-meta">Action</div>
            <div>{isOnboard ? "First-time link" : "Force fresh pairing"}</div>
          </div>
        </div>
      </div>

      <div style={{ fontSize: 13, color: "var(--iris-fg-3)", lineHeight: 1.55 }}>
        Clicking <strong>{verb}</strong> will:{" "}
        {isOnboard
          ? `create the avatar's client on the scraper VM, spawn the ${platformLabel} client, and surface the ${codeWord} here`
          : `wipe the existing session on the scraper VM, spawn the ${platformLabel} client, and surface the ${codeWord} here`}{" "}
        within ~5–15&nbsp;seconds. Make sure the avatar phone is unlocked and {platformLabel} is
        open before you click.
      </div>

      {error && (
        <div
          role="alert"
          style={{
            marginTop: 16,
            padding: "10px 12px",
            borderRadius: 8,
            background: "var(--iris-danger-bg, rgba(220,38,38,0.10))",
            border: "1px solid var(--iris-danger-line, rgba(220,38,38,0.45))",
            color: "var(--iris-danger-fg, #dc2626)",
            fontSize: 13,
            lineHeight: 1.5,
          }}
        >
          {error}
        </div>
      )}

      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 8,
          marginTop: 20,
        }}
      >
        <button type="button" className="iris-btn" onClick={onCancel} disabled={isPending}>
          Cancel
        </button>
        <button
          type="button"
          className="iris-btn iris-btn--primary"
          onClick={onStart}
          disabled={isPending}
        >
          {isPending ? (isOnboard ? "Connecting…" : "Starting…") : verb}
        </button>
      </div>
    </div>
  );
}
